import { enableOutOfTheBoxRoles } from '@gonfalon/dogfood-flags/dogfood-flags';
// eslint-disable-next-line no-restricted-imports
import { fromJS, List, Map, OrderedSet, Record } from '@gonfalon/immutable-shim/immutableShim';
import {
  isEmail,
  isInRange,
  isNotEmpty,
  isValidEmailList,
  validateRecord,
} from '@gonfalon/legacy-form-validation/validationUtils';
import type { CreateFunctionInput } from '@gonfalon/legacy-immutable/immutableUtils';

type BulkMemberInviteType = {
  emails: OrderedSet<string>;
  successfulEmails: OrderedSet<string>;
  failedEmails: Map<string, List<string>>;
  role: string;
  customRoles: List<string>;
  teamKeys: List<string>;
  roleAttributes: Map<string, List<string>>;
};

export class BulkMemberInvite extends Record<BulkMemberInviteType>({
  emails: OrderedSet(),
  failedEmails: Map(),
  successfulEmails: OrderedSet(),
  role: 'reader',
  customRoles: List(),
  teamKeys: List(),
  roleAttributes: Map(),
}) {
  isEmpty() {
    return this.emails.isEmpty();
  }

  validateSeatLimit(seatsLeft: number | null) {
    return () => {
      if (seatsLeft !== null && this.size() > seatsLeft) {
        // The message is handled separately in the UI
        return { for: 'emails', errors: [] };
      } else {
        return null;
      }
    };
  }

  validate(seatsLeft: number | null) {
    return validateRecord(this, isNotEmpty('emails'), isValidEmailList('emails'), this.validateSeatLimit(seatsLeft));
  }

  toRep() {
    const defaultBaseRole = enableOutOfTheBoxRoles() ? 'no_access' : 'reader';

    return this.emails.map((email) => ({
      email,
      role: this.role === 'custom' ? defaultBaseRole : this.role,
      customRoles: this.customRoles,
      teamKeys: this.teamKeys,
      roleAttributes: this.roleAttributes,
    }));
  }

  size(): number {
    return this.emails.size;
  }
}

type SuggestInviteMembersType = {
  emails: OrderedSet<string>;
};

export class SuggestInviteMembers extends Record<SuggestInviteMembersType>({
  emails: OrderedSet(),
}) {
  isEmpty() {
    return this.emails.isEmpty();
  }

  validate() {
    return validateRecord(this, isNotEmpty('emails'), isValidEmailList('emails'));
  }
}

export function createBulkMemberInvite(props: CreateFunctionInput<BulkMemberInvite> = {}) {
  return props instanceof BulkMemberInvite
    ? props
    : new BulkMemberInvite(fromJS(props))
        .update('emails', (emails) => emails.toOrderedSet())
        .update('failedEmails', (failedEmails) => failedEmails.toMap());
}

export function createSuggestInviteMembers(props: CreateFunctionInput<SuggestInviteMembers> = {}) {
  return props instanceof SuggestInviteMembers
    ? props
    : new SuggestInviteMembers(fromJS(props)).update('emails', (emails) => emails.toOrderedSet());
}

export type RequestSeatsFormType = {
  memberFirstName: string;
  memberLastName: string;
  memberEmail: string;
  numberOfSeats: number;
  description: string;
};

export class RequestSeatsFormRecord extends Record<RequestSeatsFormType>({
  memberFirstName: '',
  memberLastName: '',
  memberEmail: '',
  numberOfSeats: 0,
  description: '',
}) {
  validate() {
    return validateRecord(
      this,
      isNotEmpty('memberFirstName'),
      isNotEmpty('memberLastName'),
      isNotEmpty('memberEmail'),
      isEmail('memberEmail'),
      isNotEmpty('numberOfSeats'),
      isInRange({ min: 1 })('numberOfSeats'),
    );
  }
}

export function createNewRequestSeatsForm(props = {}) {
  return new RequestSeatsFormRecord(props);
}
